import { Link } from "@circleco/compass/components/Link";
import { Modal } from "@circleco/compass/components/Modal";
import { Skeleton } from "@circleco/compass/components/Skeleton";
import { Typography } from "@circleco/compass/components/Typography";

import { t } from "@/i18n-js/instance";
import { ModalPreviewTemplate } from "@/react/components/Paywalls/Checkout/CheckoutPage/CheckoutForm/PricePreview/DefaultFullPreview/ModalPreviewTemplate";
import { ConfirmAndDeclineButton } from "@/react/components/Paywalls/CheckoutWithSession/pages/OfferPage/ConfirmAndDeclineButton";
import { OFFER_PAGE_LOCALE_ROOT } from "@/react/components/Paywalls/CheckoutWithSession/pages/OfferPage/constants";
import { useConfirmAndDeclineButton } from "@/react/components/Paywalls/CheckoutWithSession/pages/OfferPage/hooks/useConfirmAndDeclineButton";
import { useOfferPageContext } from "@/react/components/Paywalls/CheckoutWithSession/pages/OfferPage/OfferPageProvider";
import { formatPaywallCurrencyAmount } from "@/react/helpers/paywalls/currencies";
import type { PaywallCheckoutPreview } from "@/react/types";

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isCheckoutPreview = (value: unknown): value is PaywallCheckoutPreview => {
  if (!isRecord(value)) return false;

  return (
    typeof value.amount_due_now === "number" &&
    isRecord(value.currency) &&
    typeof value.currency.code === "string" &&
    typeof value.currency.symbol === "string" &&
    isRecord(value.first_payment) &&
    isRecord(value.paywall_price) &&
    typeof value.paywall_price.price_type === "string"
  );
};

export const AddonStickyActions = () => {
  const { previewMutation, couponPreviewMutation, isPreviewing } =
    useOfferPageContext();
  const { handleConfirm, shouldBlockButtons, isProcessingAttempt } =
    useConfirmAndDeclineButton();
  const previewData: unknown = previewMutation.data;
  const preview = isCheckoutPreview(previewData) ? previewData : undefined;
  const isRefreshing =
    !preview || previewMutation.isPending || couponPreviewMutation.isPending;
  const orderDetailsLink = (
    <Link
      asButton
      type="button"
      variant="custom"
      typography="body-sm"
      color="primary"
      disabled={isRefreshing}
      data-testid="addon-view-order-details"
    >
      <span className="underline">{t("view_order_details")}</span>
    </Link>
  );

  return (
    <div className="w-full max-w-xl space-y-2 [@container(min-width:768px)]:space-y-3">
      <div
        className="space-y-2 [@container(min-width:768px)]:space-y-3"
        aria-live="polite"
        aria-busy={isRefreshing}
      >
        <div
          className="flex justify-between gap-4"
          data-testid="addon-financial-summary"
        >
          <Typography variant="body-md">
            <strong>{t("paywall_checkout.amount_due")}</strong>
          </Typography>
          {isRefreshing ? (
            <Skeleton shape="pill" className="h-5 w-24" />
          ) : (
            <Typography variant="body-md">
              <strong>
                {formatPaywallCurrencyAmount(
                  preview.currency,
                  preview.amount_due_now,
                )}
              </strong>
            </Typography>
          )}
        </div>
        <div className="flex items-center justify-between gap-4 [@container(min-width:768px)]:pb-4">
          {isPreviewing || !preview ? (
            orderDetailsLink
          ) : (
            <Modal.Root size="md">
              <Modal.Trigger>{orderDetailsLink}</Modal.Trigger>
              <Modal.Content data-testid="addon-order-details-modal">
                <Modal.Header title={t("paywall_checkout.order_summary")} />
                <Modal.Body>
                  <ModalPreviewTemplate preview={preview} />
                </Modal.Body>
                <Modal.Footer
                  layout="full-width"
                  primaryAction={{
                    label: t([
                      OFFER_PAGE_LOCALE_ROOT,
                      "confirm_add_to_my_order",
                    ]),
                    onClick: handleConfirm,
                    disabled: shouldBlockButtons,
                    loading: isProcessingAttempt,
                    shouldCloseModal: false,
                  }}
                />
              </Modal.Content>
            </Modal.Root>
          )}
        </div>
      </div>
      <ConfirmAndDeclineButton layout="sticky" />
    </div>
  );
};
